import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./OutlawsLoot.css";
import {
  IMG, JACKPOT_METER, EFFECT, SYMBOL, symbolSrc, SPIN_STRIP,
  PAYLINE_SVG, PAYLINE_CELLS, PAYLINE_COLOR,
} from "./assets";
import { olAudio } from "./audio";
import { announceSpin } from "../../utils/spinBreakdown";
import { DailySuperChip } from "../../components/DailySuperMeter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OL = `${API}/outlaws-loot`;
axios.defaults.withCredentials = true;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
// Owner request: keep the branded boot/loading screen up for ~12s.
const MIN_LOADING_MS = 12000;
const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const TIERS = ["MINI", "MINOR", "MAJOR", "MEGA"];
const EMPTY = [["", "", ""], ["", "", ""], ["", "", ""]];

export default function OutlawsLoot() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [bal, setBal] = useState(null);
  const [bet, setBet] = useState(1.0);
  const [pools, setPools] = useState(null);

  const [grid, setGrid] = useState(EMPTY);            // grid[reel] = [top,mid,bottom]
  const [spinReels, setSpinReels] = useState([false, false, false]);
  const [settle, setSettle] = useState([false, false, false]);
  const [nudgeReel, setNudgeReel] = useState(null);
  const [active, setActive] = useState([]);           // [[reel,row],...]
  const [activeLines, setActiveLines] = useState([]); // payline indices

  const [nudgeBanner, setNudgeBanner] = useState(null);
  const [freeMode, setFreeMode] = useState(false);
  const [freeRemaining, setFreeRemaining] = useState(0);
  const [freeTransition, setFreeTransition] = useState(false);
  const [retrigger, setRetrigger] = useState(false);
  const [jpWin, setJpWin] = useState(null);

  const [winMeter, setWinMeter] = useState(0);
  const [awaitBegin, setAwaitBegin] = useState(false);   // gate: player starts the bonus
  const beginResolver = useRef(null);
  const beginFreePlay = () => { if (beginResolver.current) { const r = beginResolver.current; beginResolver.current = null; r(); } };
  const [winPop, setWinPop] = useState(null);            // {amount} glow celebration
  const bigWinAudio = useRef(null);
  const stingAudio = useRef(null);
  const bonusAudio = useRef(null);

  // Stop ALL Outlaw's Loot audio the moment we leave the game (lobby button,
  // browser back, or any route change unmounts this component).
  useEffect(() => () => {
    try { olAudio.stopAll(); } catch (_e) { /* ignore */ }
    for (const r of [bigWinAudio, stingAudio, bonusAudio]) {
      try { if (r.current) { r.current.pause(); r.current.currentTime = 0; } } catch (_e) { /* ignore */ }
    }
  }, []);
  const popWin = async (amount, ms = 2100) => {
    if (!(amount > 0)) return;
    const big = amount >= 5 * bet;          // "big win" = 5x bet or more
    if (big) {
      try {
        if (!bigWinAudio.current) bigWinAudio.current = new Audio("/games/outlaws-loot/assets/audio/bigwin.m4a");
        bigWinAudio.current.currentTime = 0; bigWinAudio.current.volume = 0.9;
        bigWinAudio.current.play().catch(() => {});
      } catch (e) {}
    } else {
      olAudio.play("base_win");
    }
    setWinPop({ amount, big });
    await sleep(big ? Math.max(ms, 2600) : ms);
    setWinPop(null);
  };
  const [msg, setMsg] = useState("");
  const [spinning, setSpinning] = useState(false);
  const [muted, setMuted] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [menuView, setMenuView] = useState("root");
  const [isAdmin, setIsAdmin] = useState(false);
  const busy = useRef(false);
  const cridRef = useRef(null);
  const [ready, setReady] = useState(false);
  // Auto-play (auto spins)
  const autoRef = useRef({ active: false, remaining: 0, infinite: false });
  const [autoState, setAutoState] = useState({ active: false, remaining: 0, infinite: false });
  const [autoMenu, setAutoMenu] = useState(false);
  const creditRef = useRef(0);
  const [autoStopBonus, setAutoStopBonus] = useState(false);
  const [autoStopBigWin, setAutoStopBigWin] = useState(false);
  const lastBonusRef = useRef(false);
  const lastTotalWinRef = useRef(0);
  // Manual skill task (server-authoritative). null when not awaiting a decision.
  const [skill, setSkill] = useState(null); // {spin_id, awaiting:[], locked:[], remaining, stage}
  const [nudge, setNudge] = useState(null); // {reel, dir:'up'|'down', strip:[4 syms]} — 1-symbol slide
  const quickStopRef = useRef(false);       // set when SPIN is pressed mid-spin to slam-stop the reels
  const [moved, setMoved] = useState(false); // ONE nudge per spin (skill OR cosmetic), then locked
  const movedRef = useRef(false);
  const [showCoach, setShowCoach] = useState(false); // one-time "you get one nudge" hint
  const markMoved = () => { movedRef.current = true; setMoved(true); setShowCoach(false); try { localStorage.setItem("ol_nudge_coach_seen", "1"); } catch (e) {} };
  const resetMoved = () => { movedRef.current = false; setMoved(false); };
  const dismissCoach = () => { setShowCoach(false); try { localStorage.setItem("ol_nudge_coach_seen", "1"); } catch (e) {} };

  const preloadArt = () => {
    const critical = [
      IMG.background, IMG.cabinet, IMG.logo, IMG.mascot, IMG.betCoin,
      IMG.creditMeter, IMG.betMeter, IMG.winMeter,
      ...Object.values(JACKPOT_METER), ...Object.values(SYMBOL),
    ];
    return Promise.all(critical.map((src) => new Promise((res) => {
      const im = new Image(); im.onload = res; im.onerror = res; im.src = src;
      setTimeout(res, 4000);
    })));
  };

  const loadJackpots = async () => {
    try { const { data } = await axios.get(`${OL}/jackpots`); setPools(data); } catch (_e) { /* ignore */ }
  };

  useEffect(() => {
    const bootStart = Date.now();
    (async () => {
      try {
        const [{ data }] = await Promise.all([axios.get(`${OL}/config`), preloadArt()]);
        setCfg(data);
        setBet(data.bet_menu.includes(1.0) ? 1.0 : data.bet_menu[0]);
        setPools(data.jackpots);
        await resume();
        try { const me = await axios.get(`${API}/auth/me`); setIsAdmin(me.data?.role === "admin"); } catch (_e) { /* player */ }
        const wait = MIN_LOADING_MS - (Date.now() - bootStart);
        if (wait > 0) await sleep(wait);
        setReady(true);
      } catch (e) {
        setMsg("Failed to load Outlaw's Loot.");
        const wait = Math.min(3000, MIN_LOADING_MS - (Date.now() - bootStart));
        if (wait > 0) await sleep(wait);
        setReady(true);
      }
    })();
    const t = setInterval(loadJackpots, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  // Keep a live ref of playable credit so the auto-play loop reads a fresh balance.
  useEffect(() => { creditRef.current = bal ? bal.playable_total : 0; }, [bal]);

  // Lease countdown (does not restart on refresh — server owns the deadline).
  useEffect(() => {
    if (!skill || skill.remaining == null) return;
    const t = setInterval(() => setSkill((s) => (s ? { ...s, remaining: Math.max(0, (s.remaining || 0) - 1) } : s)), 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [skill?.spin_id]);

  // Refresh/reconnect-safe: pull wallet + last completed spin and re-present statically.
  const resume = async () => {
    try {
      const { data } = await axios.get(`${OL}/state`);
      setBal(data.balances);
      if (data.last_spin) {
        const r = data.last_spin;
        if (r.status === "AWAITING_SKILL" || r.status === "SKILL_IN_PROGRESS") {
          enterSkill(r);   // resume the exact unresolved task
          return;
        }
        setGrid(r.base.final_window);
        setActive(cellsFromLineWins(r.base.line_wins));
        setActiveLines((r.base.line_wins || []).map((w) => w.payline));
        setWinMeter(r.total_win);
        setMsg(r.total_win > 0 ? `Last win ${money(r.total_win)}` : "");
      }
    } catch (_e) { /* ignore */ }
  };

  const cellsFromLineWins = (lineWins) =>
    (lineWins || []).flatMap((w) => {
      const cells = w.cells || PAYLINE_CELLS[w.payline] || [1, 1, 1];
      return cells.map((row, reel) => [reel, row]);
    });

  const countUp = async (to, ms = 500) => {
    const from = 0; const steps = 18; const step = (to - from) / steps;
    for (let i = 1; i <= steps; i++) { setWinMeter(Math.min(to, from + step * i)); await sleep(ms / steps); }
    setWinMeter(to);
  };

  const eligible = (tier) => {
    const min = pools?.min_bet_by_tier?.[tier] ?? 0;
    return bet >= min;
  };

  // ── Reel presentation ─────────────────────────────────────────────
  // Start the reels spinning IMMEDIATELY on tap (snappy) — the landing happens
  // when the server result arrives.
  const startReels = () => {
    setActive([]); setActiveLines([]); setNudgeBanner(null); setNudgeReel(null);
    setSettle([false, false, false]);
    setSpinReels([true, true, true]);
    olAudio.play("reel_spin_start");
  };

  // Straight slot: reels stop ONE-TWO-THREE (left→right) with a clear gap, and a
  // stop sound fires exactly as each reel lands. Whatever lands is what pays.
  const spinTo = async (spin) => {
    const final = spin.final_window || spin.initial_window;
    for (let reel = 0; reel < 3; reel++) {
      await sleep(quickStopRef.current ? 0 : 265);
      setSpinReels((s) => { const n = [...s]; n[reel] = false; return n; });
      setGrid((g) => { const n = g.map((c) => [...c]); n[reel] = [...final[reel]]; return n; });
      setSettle((s) => { const n = [...s]; n[reel] = true; return n; });
      olAudio.play("reel_stop");
    }
    await sleep(quickStopRef.current ? 0 : 90);
  };

  // Free-play landing: spin builds up smoothly, then reels stop LEFT→RIGHT and
  // land DIRECTLY on the final board (no nudge/reveal animation — wherever it
  // lands is what pays, evaluated on all 5 free-play lines). Slower + cleaner so
  // the feature feels intimate.
  const freeSpinTo = async (spin) => {
    const final = spin.final_window || spin.initial_window;
    for (let reel = 0; reel < 3; reel++) {
      await sleep(230);
      setSpinReels((s) => { const n = [...s]; n[reel] = false; return n; });
      setGrid((g) => { const n = g.map((c) => [...c]); n[reel] = [...final[reel]]; return n; });
      setSettle((s) => { const n = [...s]; n[reel] = true; return n; });
      olAudio.play("reel_stop");
    }
    await sleep(180);
  };

  const showWins = async (spin, lineCountUpTo) => {
    const lw = spin.line_wins || [];
    setGrid(spin.final_window);
    if (nudgeBanner) { /* banner cleared by next spin */ }
    if (lw.length) {
      setActive(cellsFromLineWins(lw));
      setActiveLines(lw.map((w) => w.payline));
      olAudio.play("base_win");
      const sum = lw.reduce((a, w) => a + (w.amount || 0), 0);
      if (typeof lineCountUpTo === "number") await countUp(lineCountUpTo, 520);
      setMsg(`Line win ${money(sum)}`);
      await sleep(650);
    }
  };

  const celebrateJackpot = async (tier, amount) => {
    setJpWin({ tier, amount });
    olAudio.play(`jackpot_${tier.toLowerCase()}`);
    setMsg(`${tier} JACKPOT — ${money(amount)}!`);
    await sleep(2200);
    setJpWin(null);
  };

  const jackpotsFromResponse = (r) =>
    (r.jackpots || []).filter((j) => j.qualified && j.amount > 0);

  const runFreePlay = async (feature, allJackpots) => {
    // Fade the saloon background out and fire a short celebratory STING the
    // instant the 3 BONUS land — the free-play music holds off until BEGIN.
    olAudio.pauseAmbient();
    try {
      if (!stingAudio.current) stingAudio.current = new Audio("/games/outlaws-loot/assets/audio/bonus.m4a");
      stingAudio.current.currentTime = 0; stingAudio.current.volume = 0.95;
      stingAudio.current.play().catch(() => {});
    } catch (e) {}
    // Let the 3-BONUS board sit for a couple seconds (with the sting) before
    // the Free Play intro sign pops up.
    setMsg("3 BONUS — GOLD RUSH!");
    await sleep(2200);
    setMsg("BONUS! Gold Rush unlocked — hit BEGIN when you're ready");
    setFreeMode(true);
    // Wait for the player to start the feature (no auto-start).
    setAwaitBegin(true);
    // During auto-play, auto-start the bonus so spins stay hands-free.
    if (autoRef.current.active) setTimeout(() => beginFreePlay(), 1600);
    await new Promise((res) => { beginResolver.current = res; });
    setAwaitBegin(false);
    // Free-play music kicks in now — cut the sting first for a clean hand-off.
    try { if (stingAudio.current) { stingAudio.current.pause(); stingAudio.current.currentTime = 0; } } catch (e) {}
    try {
      if (!bonusAudio.current) bonusAudio.current = new Audio("/games/outlaws-loot/assets/audio/western_bonus.wav");
      bonusAudio.current.loop = true;
      bonusAudio.current.currentTime = 0; bonusAudio.current.volume = 0.7;
      bonusAudio.current.play().catch(() => {});
    } catch (e) {}
    setFreeTransition(true);
    olAudio.play("free_play_start");
    await sleep(1200);
    setFreeTransition(false);
    const spins = feature.spins || [];
    let jpIdx = 0;
    for (let i = 0; i < spins.length; i++) {
      const sp = spins[i];
      setFreeRemaining(spins.length - i);
      startReels();
      // Longer, continuous spin so the reels feel as smooth as base play.
      await sleep(quickStopRef.current ? 0 : 780);
      await freeSpinTo(sp);
      const spSum = (sp.line_wins || []).reduce((a, w) => a + (w.amount || 0), 0);
      await showWins(sp, undefined);
      if (spSum > 0) setWinMeter((w) => w + spSum);
      if (sp.bonus) {
        setRetrigger(true); olAudio.play("free_play_retrigger");
        setMsg("RETRIGGER! +5 Free Plays");
        await sleep(1100); setRetrigger(false);
      }
      if (sp.jackpot_tier) {
        const j = allJackpots[jpIdx];
        if (j) { setWinMeter((w) => w + j.amount); await celebrateJackpot(j.tier, j.amount); jpIdx++; }
      }
      // Dwell between free spins so each result is readable (owner: was too fast).
      await sleep(spSum > 0 ? 900 : 720);
    }
    setFreeRemaining(0);
    setFreeMode(false);
    try { if (bonusAudio.current) { bonusAudio.current.pause(); bonusAudio.current.currentTime = 0; } } catch (e) {}
    olAudio.resumeAmbient();
    setMsg("Free Plays complete — back to the saloon.");
    await sleep(700);
    return jpIdx;
  };

  const presentSpin = async (r) => {
    setJpWin(null); setWinMeter(0); setRetrigger(false);
    const base = r.base;
    await spinTo(base);
    const baseLineSum = (base.line_wins || []).reduce((a, w) => a + (w.amount || 0), 0);
    await showWins(base, baseLineSum > 0 ? baseLineSum : undefined);

    const allJp = jackpotsFromResponse(r);
    let jpConsumed = 0;
    // Base-game jackpots fire before the feature; feature jackpots inside it.
    const baseJp = allJp.filter((j) => j.context === "base");
    for (const j of baseJp) { await celebrateJackpot(j.tier, j.amount); jpConsumed++; }

    if (r.feature) {
      const featureJp = allJp.filter((j) => j.context !== "base");
      await runFreePlay(r.feature, featureJp);
    }

    setWinMeter(r.total_win);
    setMsg(r.total_win > 0 ? `WIN ${money(r.total_win)}!` : "");
    await popWin(r.total_win);
  };


  // ── MANUAL skill task (RAISE=up/bottom→centre, LOWER=down/top→centre) ──
  const landReels = async (win) => {
    for (let reel = 0; reel < 3; reel++) {
      await sleep(quickStopRef.current ? 0 : 110);
      setSpinReels((s) => { const n = [...s]; n[reel] = false; return n; });
      setGrid((g) => { const n = g.map((c) => [...c]); n[reel] = [...win[reel]]; return n; });
      setSettle((s) => { const n = [...s]; n[reel] = true; return n; });
      olAudio.play("reel_stop");
    }
  };

  const enterSkill = (r) => {
    setSpinReels([false, false, false]); setSettle([true, true, true]);
    setActive([]); setActiveLines([]); setNudgeBanner(null); setNudgeReel(null);
    setJpWin(null); setWinMeter(0);
    if (r.stage === "free_play") setFreeMode(true);
    setGrid(r.skill.current_window || r.skill.initial_window);
    setSkill({ spin_id: r.spin_id, awaiting: r.skill.reels_awaiting_decision || [],
               locked: r.skill.locked_reels || [], remaining: r.skill.lease_seconds_remaining,
               stage: r.stage || "base" });
    setMsg("Press Raise or Lower to Move a Symbol");
    try { if (!localStorage.getItem("ol_nudge_coach_seen")) setShowCoach(true); } catch (e) {}
    setSpinning(false); busy.current = false;
  };

  const finalizeSkill = async (r) => {
    const won = r.skill_result === "WON" || r.status === "SKILL_WON";
    setSkill(null); setNudge(null); setNudgeReel(null); setFreeRemaining(0);
    const base = r.base;
    if (won) {
      // Option (a): the player's move produced the ACTUAL winning board — show
      // exactly what they landed and light up the centre payline.
      const winGrid = r.skill_result_window || base.final_window;
      setGrid(winGrid);
      const centerWin = (r.regular_win || 0) > 0;
      setActive(centerWin ? [[0, 1], [1, 1], [2, 1]] : []);
      setActiveLines(centerWin ? [0] : []);
      if (centerWin) olAudio.play("base_win");
      const baseJp = (r.jackpots || []).filter((j) => j.context === "base" && j.status === "SETTLED" && j.amount > 0);
      for (const j of baseJp) await celebrateJackpot(j.tier, j.amount);
      // Skill is OFF during the bonus: the feature auto-played server-side — present it.
      if (r.feature) {
        const featureJp = (r.jackpots || []).filter((j) => j.context !== "base" && j.status === "SETTLED" && j.amount > 0);
        await runFreePlay(r.feature, featureJp);
      }
      setWinMeter(r.total_win);
      setMsg(r.total_win > 0 ? `WIN ${money(r.total_win)}!` : "");
      await popWin(r.total_win);
    } else {
      // LOSS: keep the player's own (botched) board — never reveal the un-earned win.
      if (r.skill_result_window) setGrid(r.skill_result_window);
      setActive([]); setActiveLines([]); setWinMeter(0);
      setMsg("No win — line up the next one!");
    }
    if (typeof r.balance_after === "number") setBal((b) => ({ ...(b || {}), playable_total: r.balance_after }));
    announceSpin(r.bet, won ? r.total_win : 0);
    loadJackpots();
  };

  const handleSkillResponse = async (r) => {
    if (r.status === "AWAITING_SKILL" || r.status === "SKILL_IN_PROGRESS") {
      if (r.skill) {
        if (r.stage === "free_play") setFreeMode(true);
        setGrid(r.skill.current_window || r.skill.initial_window);
        setSkill({ spin_id: r.spin_id, awaiting: r.skill.reels_awaiting_decision || [],
                   locked: r.skill.locked_reels || [], remaining: r.skill.lease_seconds_remaining,
                   stage: r.stage || "base" });
        setMsg("Press Raise or Lower to Move a Symbol");
        try { if (!localStorage.getItem("ol_nudge_coach_seen")) setShowCoach(true); } catch (e) {}
      }
      return;
    }
    await finalizeSkill(r);
  };

  // Cosmetic nudge on a NON-skill board (losing/idle): the reel physically moves
  // one symbol so the buttons always feel "live", but NOTHING is calculated — the
  // player can still just press SPIN to move on. Wraps the 3 visible symbols so the
  // move is self-consistent (loss ⇒ result is display-only, never scored).
  const cosmeticNudge = async (reel, action) => {
    if (movedRef.current) return;              // one nudge per spin — then locked until next spin
    busy.current = true;
    markMoved();
    olAudio.unlock(); olAudio.play("button_press"); olAudio.play("nudge_step");
    const from = grid[reel];
    const to = action === "LOWER" ? [from[2], from[0], from[1]] : [from[1], from[2], from[0]];
    const dir = action === "LOWER" ? "down" : "up";
    const strip = dir === "down"
      ? [to[0], from[0], from[1], from[2]]
      : [from[0], from[1], from[2], to[2]];
    setActive([]); setActiveLines([]);
    setNudge({ reel, dir, strip });
    await sleep(300);
    setGrid((g) => { const n = g.map((c) => [...c]); n[reel] = [...to]; return n; });
    setNudge(null);
    olAudio.play("reel_stop");
    busy.current = false;
  };

  const pressSkill = async (reel, action) => {
    if (busy.current || spinning || freeMode) return;
    if (!skill) { await cosmeticNudge(reel, action); return; }   // loss/idle → move only, no calc
    if (!skill.awaiting.includes(reel)) return;
    if (movedRef.current) return;               // one move per spin
    busy.current = true;
    olAudio.unlock(); olAudio.play("button_press"); olAudio.play("nudge_step");
    try {
      const { data } = await axios.post(`${OL}/skill-action`, {
        spin_id: skill.spin_id, reel_index: reel, action, decision_idempotency_key: uuid() });
      markMoved();
      // The board AFTER this single move (genuine reel-strip neighbours):
      const resolved = data.skill ? (data.skill.current_window || data.skill.initial_window)
        : (data.skill_result_window || (data.base && data.base.final_window));
      const from = grid[reel];
      const to = (resolved && resolved[reel]) || from;
      const won = data.skill_result === "WON" || data.status === "SKILL_WON" || (data.regular_win || 0) > 0;
      const noMove = JSON.stringify(to) === JSON.stringify(from);
      // (b) A winning nudge must SETTLE exactly on the win and lock — never glide
      // past it. If the win is already centred (bar-saturated staged board), skip
      // the no-op slide and lock straight onto the win.
      if (won && noMove) {
        olAudio.play("reel_stop");
        await sleep(120);
        await handleSkillResponse(data);
        return;
      }
      // Simple ONE-symbol slide of JUST this reel — no spin, no other reel moves.
      // LOWER = symbols slide DOWN (top neighbour drops into centre).
      // RAISE = symbols slide UP  (bottom neighbour rises into centre).
      const dir = action === "LOWER" ? "down" : "up";
      const strip = dir === "down"
        ? [to[0], from[0], from[1], from[2]]
        : [from[0], from[1], from[2], to[2]];
      setNudge({ reel, dir, strip });
      await sleep(300);
      setGrid((g) => { const n = g.map((c) => [...c]); n[reel] = [...to]; return n; });
      setNudge(null);
      olAudio.play("reel_stop");
      await sleep(80);
      await handleSkillResponse(data);
    } catch (e) {
      setNudge(null);
      const d = e?.response?.data?.detail;
      setMsg(typeof d === "string" ? d : "Move failed — try again.");
    } finally { busy.current = false; }
  };

  // ── Auto-play (auto spins) ───────────────────────────────────────────────
  const syncAuto = () => setAutoState({ ...autoRef.current });
  const stopAuto = () => { autoRef.current = { active: false, remaining: 0, infinite: false }; syncAuto(); };
  const runAutoLoop = async () => {
    while (autoRef.current.active) {
      if (!autoRef.current.infinite && autoRef.current.remaining <= 0) break;
      if (creditRef.current < bet) { setMsg("Out of credits — auto play stopped."); break; }
      await doSpin();
      if (!autoRef.current.active) break;
      if (autoRef.current.stopOnBonus && lastBonusRef.current) { setMsg("Auto play stopped — bonus triggered!"); break; }
      if (autoRef.current.stopOnBigWin && lastTotalWinRef.current >= 5 * bet) { setMsg("Auto play stopped — big win!"); break; }
      if (!autoRef.current.infinite) { autoRef.current.remaining -= 1; syncAuto(); }
      await sleep(700);
    }
    stopAuto();
  };
  const startAuto = (n) => {
    setAutoMenu(false);
    if (busy.current || spinning || skill || freeMode) return;
    if ((bal ? bal.playable_total : 0) < bet) { setMsg("Not enough credits to auto play."); return; }
    olAudio.unlock(); olAudio.play("button_press");
    autoRef.current = { active: true, remaining: n === Infinity ? 0 : n, infinite: n === Infinity,
                        stopOnBonus: autoStopBonus, stopOnBigWin: autoStopBigWin };
    syncAuto();
    runAutoLoop();
  };

  const doSpin = async () => {
    if (busy.current || !cfg || skill) return;
    quickStopRef.current = false;
    resetMoved();
    olAudio.unlock(); olAudio.startAmbient();
    olAudio.play("button_press");
    busy.current = true; setSpinning(true);
    startReels();
    const t0 = Date.now();
    try {
      const q = new URLSearchParams(window.location.search);
      const previewSeed = q.get("preview_seed"); // admin-only simulation
      let data;
      if (previewSeed && isAdmin) {
        const res = await axios.get(`${OL}/dev/preview`, { params: { seed: Number(previewSeed), bet, mode: q.get("preview_mode") || cfg.mode } });
        data = res.data;
      } else {
        // Reuse the same client_request_id until a spin succeeds so a retry after
        // a network failure replays idempotently against the V6 backend.
        if (!cridRef.current) cridRef.current = uuid();
        const res = await axios.post(`${OL}/spin`, { bet, client_request_id: cridRef.current });
        data = res.data;
        cridRef.current = null;
        setBal((b) => ({ ...(b || {}), playable_total: data.balance_after }));
      }
      const elapsed = Date.now() - t0;
      const floor = quickStopRef.current ? 0 : 200;
      if (elapsed < floor) await sleep(floor - elapsed);
      if (data.status === "AWAITING_SKILL") {   // manual skill task: pause for the player
        await landReels(data.skill.initial_window);
        enterSkill(data);
        loadJackpots();
        return;
      }
      await presentSpin(data);
      if (!data.preview) announceSpin(data.bet, data.total_win);
      lastBonusRef.current = !!(data.feature || data.bonus_triggered);
      lastTotalWinRef.current = data.total_win || 0;
      loadJackpots();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const clean = (typeof d === "string" && !/admin|preview|dev/i.test(d)) ? d : "Spin failed. Please try again.";
      setSpinReels([false, false, false]);
      setMsg(clean);
    } finally { busy.current = false; setSpinning(false); }
  };

  // Admin-only QA: force a rich bonus outcome (seeded preview — no wallet effect)
  // so the whole Free-Play feature can be watched on demand.
  const qaBonus = async () => {
    if (busy.current || spinning || skill || freeMode) return;
    quickStopRef.current = false;
    resetMoved();
    olAudio.unlock(); olAudio.startAmbient(); olAudio.play("button_press");
    busy.current = true; setSpinning(true);
    startReels();
    const t0 = Date.now();
    try {
      const res = await axios.post(`${OL}/qa-bonus`);
      const elapsed = Date.now() - t0;
      if (elapsed < 360) await sleep(360 - elapsed);
      await presentSpin(res.data);
      loadJackpots();
    } catch (e) {
      setSpinReels([false, false, false]);
      setMsg("QA bonus preview failed.");
    } finally { busy.current = false; setSpinning(false); }
  };

  const changeBet = (dir) => {
    if (spinning || !cfg) return;
    const menu = cfg.bet_menu;
    const idx = menu.indexOf(bet);
    const next = Math.min(menu.length - 1, Math.max(0, idx + dir));
    if (next !== idx) { setBet(menu[next]); olAudio.unlock(); olAudio.play("bet_change"); }
  };

  const toggleMute = () => {
    olAudio.unlock();
    setMuted((m) => { const nm = !m; olAudio.setMuted(nm); if (!nm) olAudio.startAmbient(); return nm; });
    olAudio.play("button_press");
  };

  const jackpotValue = (tier) => {
    if (pools?.type === "online_shared") return money(pools.pools?.[tier]?.current);
    const mult = cfg?.jackpots?.multiples?.[tier];
    return mult ? `${mult}x` : "—";
  };

  if (!cfg || !ready) return (
    <div className="ol-root" data-testid="outlaws-loot" style={{ backgroundImage: `url(${IMG.background})` }}>
      <div className="ol-scrim" />
      <div className="ol-loading" data-testid="ol-loading">
        <img src={IMG.logo} alt="Outlaw's Loot" className="ol-loading-logo" />
        <div className="ol-loading-txt">Loading Outlaw's Loot…</div>
        <div className="ol-loading-bar"><span style={{ animationDuration: `${MIN_LOADING_MS}ms` }} /></div>
      </div>
    </div>
  );

  const credit = bal ? bal.playable_total : 0;

  return (
    <div className="ol-root" data-testid="outlaws-loot" style={{ backgroundImage: `url(${IMG.background})` }}>
      <div className="ol-scrim" />
      <div className={`ol-stage ${freeMode ? "ol-freemode" : ""}`}>

        {/* Compact corner utilities (do not overlap logo/meters/reels) */}
        <button className="ol-corner ol-corner-left" onClick={() => { olAudio.unlock(); olAudio.play("button_press"); setMenuView("root"); setShowMenu(true); }}
                aria-label="Menu" data-testid="ol-menu-btn"><img src={IMG.menuBtn} alt="Menu" /></button>
        <button className={`ol-corner ol-corner-right ${muted ? "ol-muted" : ""}`} onClick={toggleMute}
                aria-label="Sound" data-testid="ol-sound-btn"><img src={IMG.soundBtn} alt="Sound" /></button>
        <button className="ol-corner-lobby" onClick={() => { olAudio.play("button_press"); if (autoRef.current.active) stopAuto(); navigate("/casino/slots"); }}
                aria-label="Back to lobby" data-testid="ol-lobby-btn">
          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V21h14V9.5" /></svg>
          LOBBY
        </button>
        {cfg?.qa_bonus && isAdmin && !freeMode && (
          <button className="ol-qa-bonus" onClick={qaBonus} disabled={spinning || !!skill}
                  data-testid="ol-qa-bonus-btn">▶ FIRE BONUS</button>
        )}

        <div className="ol-top">
        {/* Logo (base) / Gold Rush header (free mode) share the top slot */}
        {!freeMode ? (
          <img className="ol-logo" src={IMG.logo} alt="Outlaw's Loot" data-testid="ol-logo" />
        ) : (
          <div className="ol-free-head" data-testid="ol-freeplay-header">
            <img className="ol-free-head-img" src={IMG.goldRushHeader} alt="Gold Rush Free Plays" />
            <div className="ol-free-remaining" data-testid="ol-freeplays-remaining">
              <img src={IMG.freePlaysRemaining} alt="Free plays remaining" />
              <span data-testid="ol-freeplays-count">{freeRemaining}</span>
            </div>
          </div>
        )}

        {/* Jackpot meters */}
        <div className="ol-jprow" data-testid="ol-jackpot-strip" style={{ position: "relative" }}>
          <DailySuperChip style={{ transform: "translate(-50%, 0)" }} />
          {TIERS.map((t) => (
            <div className={`ol-jp ${!eligible(t) ? "ol-jp-inelig" : ""}`} key={t} data-testid={`ol-jp-${t}`}>
              <img className="ol-jp-frame" src={JACKPOT_METER[t]} alt={t} />
              <span className="ol-jp-val" data-testid={`ol-jp-val-${t}`}>{jackpotValue(t)}</span>
              {!eligible(t) && <span className="ol-jp-min" data-testid={`ol-jp-min-${t}`}>MIN {money(pools?.min_bet_by_tier?.[t] || 0)}</span>}
            </div>
          ))}
        </div>

        {/* Cabinet + reels */}
        <div className="ol-cabwrap" data-testid="ol-reels">
          <img className="ol-mascot" src={IMG.mascot} alt="" aria-hidden="true"
               data-nudge={nudgeBanner ? "1" : "0"} />
          <div className={`ol-reelarea ${skill ? "ol-skill-live" : ""}`}>
            {[0, 1, 2].map((reel) => (
              <div className={`ol-reel ${spinReels[reel] ? "ol-reel-spin" : ""} ${settle[reel] ? "ol-reel-settle" : ""} ${nudgeReel === reel ? "ol-reel-nudge" : ""}`}
                   key={reel}>
                {nudge && nudge.reel === reel ? (
                  <div className={`ol-nudge-cells ol-nudge-${nudge.dir}`} data-testid={`ol-nudge-${reel}`}>
                    {nudge.strip.map((sym, i) => (
                      <div className={`ol-cell ${sym === "BONUS" ? "ol-cell-bonus" : ""}`} key={i}>
                        {sym ? <img src={symbolSrc(sym)} alt={sym} /> : null}
                      </div>
                    ))}
                  </div>
                ) : (
                <div className="ol-reel-cells">
                  {[0, 1, 2].map((row) => {
                    const sym = grid[reel][row];
                    const isActive = active.some(([r, c]) => r === reel && c === row);
                    return (
                      <div className={`ol-cell ${isActive ? "ol-cell-win" : ""} ${sym === "BONUS" ? "ol-cell-bonus" : ""}`} key={row}
                           data-testid={`ol-cell-${reel}-${row}`}>
                        {sym ? <img src={symbolSrc(sym)} alt={sym} /> : null}
                      </div>
                    );
                  })}
                </div>
                )}
                {spinReels[reel] && (
                  <div className="ol-reel-blur">
                    <div className="ol-blurstrip">
                      {SPIN_STRIP.concat(SPIN_STRIP).map((s, i) => (
                        <div className="ol-blurcell" key={i}><img src={symbolSrc(s)} alt="" /></div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <img className="ol-cabinet" src={IMG.cabinet} alt="" aria-hidden="true" />
          {/* Payline overlays */}
          <div className="ol-payoverlay">
            {activeLines.map((pl) => (
              <img key={pl} className="ol-payline" src={PAYLINE_SVG[pl]} alt=""
                   data-testid={`ol-payline-${pl}`} />
            ))}
          </div>
          {nudgeBanner && (
            <div className="ol-nudge-banner" data-testid="ol-nudge-banner">
              GOLD HAND NUDGE · reel {nudgeBanner.reel + 1} {nudgeBanner.direction < 0 ? "▲" : "▼"}
            </div>
          )}
          {awaitBegin && (
            <div className="ol-begin" data-testid="ol-begin-overlay">
              <div className="ol-begin-inner">
                <div className="ol-begin-title">GOLD RUSH FREE PLAYS</div>
                <div className="ol-begin-sub">Ready when you are, partner.</div>
                <button className="ol-begin-btn" onClick={beginFreePlay} data-testid="ol-begin-btn">BEGIN FREE PLAY</button>
              </div>
            </div>
          )}
          {winPop && (
            <div className={`ol-winpop ${winPop.big ? "ol-winpop-big" : ""}`} data-testid="ol-win-popup">
              <div className="ol-winpop-inner">
                <div className="ol-winpop-label">{winPop.big ? "BIG WIN!" : "WIN"}</div>
                <div className="ol-winpop-amount" data-testid="ol-win-popup-amount">{money(winPop.amount)}</div>
              </div>
            </div>
          )}
          {showCoach && skill && (
            <div className="ol-coach" data-testid="ol-nudge-coach" onClick={dismissCoach}>
              <div className="ol-coach-bubble">
                <strong>You get ONE nudge</strong>
                <span>Tap RAISE or LOWER once to line the winning symbol onto the middle row.</span>
                <button className="ol-coach-ok" onClick={dismissCoach} data-testid="ol-coach-ok">Got it</button>
              </div>
            </div>
          )}
          {/* Skill controls live on the cabinet TRIM. Shown on EVERY base-game spin
              (lit even on losses — the player may just press SPIN to move on).
              Functional only while a win is awaiting a skill pick. */}
          {/* Straight slot: manual RAISE/LOWER nudge retired — wins pay on the
              lines automatically, so the skill buttons are removed. */}
        </div>

        </div>

        {/* Bonus info — how the feature & lines work */}
        <div className="ol-bonusinfo" data-testid="ol-bonus-info">
          <div className="ol-bonusinfo-syms">
            <img src={symbolSrc("BONUS")} alt="Bonus" />
            <img src={symbolSrc("BONUS")} alt="Bonus" />
            <img src={symbolSrc("BONUS")} alt="Bonus" />
          </div>
          <div className="ol-bonusinfo-text">
            <strong>Land 3 <span className="ol-hl">BONUS</span> on the <span className="ol-hl">middle line</span> to trigger Free Plays</strong>
            <span>Base game pays the <b>middle line only</b>. In the bonus, <b>all 5 lines pay</b> — top, middle, bottom &amp; both diagonals — so wins land far more often.</span>
          </div>
        </div>

        {/* Meters */}
        <div className="ol-bottom">
        <div className="ol-meters">
          <div className="ol-meter" data-testid="ol-credit-meter">
            <img src={IMG.creditMeter} alt="Credit" />
            <span className="ol-meter-val">{money(credit)}</span>
          </div>
          <div className="ol-meter" data-testid="ol-bet-meter">
            <img src={IMG.betMeter} alt="Bet" />
            <span className="ol-meter-val" data-testid="ol-bet-value">{money(bet)}</span>
          </div>
          <div className="ol-meter" data-testid="ol-win-meter">
            <img src={IMG.winMeter} alt="Win" />
            <span className={`ol-meter-val ${winMeter > 0 ? "ol-win-live" : ""}`} data-testid="ol-last-win">{money(winMeter)}</span>
          </div>
        </div>

        {/* Controls */}
        <div className="ol-controls">
          {autoMenu && (
            <div className="ol-auto-menu" data-testid="ol-auto-menu">
              <div className="ol-auto-menu-title">AUTO SPINS</div>
              <div className="ol-auto-opts">
                {[10, 25, 50, 100].map((n) => (
                  <button key={n} className="ol-auto-opt" onClick={() => startAuto(n)} data-testid={`ol-auto-${n}`}>{n}</button>
                ))}
                <button className="ol-auto-opt ol-auto-inf" onClick={() => startAuto(Infinity)} data-testid="ol-auto-inf">∞</button>
              </div>
              <div className="ol-auto-toggles">
                <button className={`ol-auto-toggle ${autoStopBonus ? "on" : ""}`} onClick={() => setAutoStopBonus((v) => !v)} data-testid="ol-auto-stop-bonus">
                  <span className="ol-auto-check">{autoStopBonus ? "✓" : ""}</span> Stop on bonus
                </button>
                <button className={`ol-auto-toggle ${autoStopBigWin ? "on" : ""}`} onClick={() => setAutoStopBigWin((v) => !v)} data-testid="ol-auto-stop-bigwin">
                  <span className="ol-auto-check">{autoStopBigWin ? "✓" : ""}</span> Stop on big win (5×+)
                </button>
              </div>
            </div>
          )}
          <button className={`ol-iconbtn ol-auto-btn ${autoState.active ? "ol-auto-on" : ""}`}
                  onClick={() => { if (autoState.active) { stopAuto(); return; } if (skill || freeMode || spinning) return; setAutoMenu((m) => !m); }}
                  disabled={!!skill || freeMode}
                  aria-label="Auto play" data-testid="ol-auto-btn">
            {autoState.active
              ? <span className="ol-auto-count">{autoState.infinite ? "∞" : autoState.remaining}<small>STOP</small></span>
              : <span className="ol-auto-lbl">AUTO</span>}
          </button>
          <button className="ol-iconbtn ol-bet-minus" onClick={() => changeBet(-1)} disabled={spinning || autoState.active}
                  aria-label="Decrease bet" data-testid="ol-bet-minus"><img src={IMG.betMinus} alt="-" /></button>
          <button className="ol-spin" onClick={() => { if (skill || freeMode) return; if (autoState.active) { stopAuto(); quickStopRef.current = true; return; } if (spinning) { quickStopRef.current = true; } else { doSpin(); } }}
                  disabled={!!skill || freeMode} data-testid="ol-spin-btn">
            <img src={IMG.betCoin} alt="" />
            <span>{skill ? "PICK" : (freeMode ? "AUTO" : (autoState.active ? "STOP" : (spinning ? "STOP" : "SPIN")))}</span>
          </button>
          <button className="ol-iconbtn ol-bet-plus" onClick={() => changeBet(1)} disabled={spinning || autoState.active}
                  aria-label="Increase bet" data-testid="ol-bet-plus"><img src={IMG.betPlus} alt="+" /></button>
        </div>

        {/* Win/status message (kept above controls; empty when idle so no "ready" filler) */}
        <div className="ol-msg" data-testid="ol-message">{msg}</div>
        {skill && skill.remaining != null && (
          <div className="ol-skill-timer" data-testid="ol-skill-timer">
            {Math.floor(skill.remaining / 60)}:{String(skill.remaining % 60).padStart(2, "0")}
          </div>
        )}
        </div>
      </div>

      {/* Free play transition */}
      {freeTransition && (
        <div className="ol-overlay ol-free-trans" data-testid="ol-freeplay-transition">
          <img className="ol-gold-dust" src={EFFECT.goldDust} alt="" />
          <img className="ol-free-trans-head" src={IMG.goldRushHeader} alt="Gold Rush Free Plays" />
          <img className="ol-start-btn" src={IMG.startFreePlay} alt="" />
        </div>
      )}

      {/* Retrigger banner */}
      {retrigger && (
        <div className="ol-overlay ol-retrigger" data-testid="ol-retrigger-banner">
          <img src={IMG.freePlayButton} alt="" />
          <div>RETRIGGER · +5 FREE PLAYS</div>
        </div>
      )}

      {/* Jackpot celebration */}
      {jpWin && (
        <div className="ol-overlay ol-jp-celebrate" data-testid="ol-jackpot-win">
          <img className="ol-jp-ring" src={EFFECT.glowRing} alt="" />
          <img className="ol-jp-burst" src={EFFECT.coinBurst} alt="" />
          <img className="ol-jp-swirl" src={EFFECT.coinSwirl} alt="" />
          <img className="ol-jp-sym" src={SYMBOL[jpWin.tier]} alt={jpWin.tier} />
          <div className="ol-jp-title" data-testid="ol-jackpot-tier">{jpWin.tier} JACKPOT</div>
          <div className="ol-jp-amt" data-testid="ol-jackpot-amount">{money(jpWin.amount)}</div>
        </div>
      )}

      {/* Menu panel (secondary utilities: Paytable, Game Rules, Sound, Close) */}
      {showMenu && (
        <div className="ol-modal-wrap" onClick={() => { setShowMenu(false); setMenuView("root"); }} data-testid="ol-menu-modal">
          <div className="ol-modal" onClick={(e) => e.stopPropagation()}>
            {menuView === "root" && (
              <>
                <h3>MENU</h3>
                <button className="ol-menu-item" onClick={() => setMenuView("paytable")} data-testid="ol-menu-paytable">PAYTABLE</button>
                <button className="ol-menu-item" onClick={() => setMenuView("rules")} data-testid="ol-menu-rules">GAME RULES</button>
                <button className="ol-menu-item" onClick={toggleMute} data-testid="ol-menu-sound-toggle">SOUND: {muted ? "OFF" : "ON"}</button>
                <button className="ol-modal-close" onClick={() => setShowMenu(false)} data-testid="ol-menu-close">CLOSE</button>
              </>
            )}
            {menuView === "paytable" && (
              <div data-testid="ol-paytable-modal">
                <h3>PAYTABLE</h3>
                <div className="ol-pt-grid">
                  {Object.entries(cfg.paytable).sort((a, b) => b[1] - a[1]).map(([sym, mult]) => (
                    <div className="ol-pt-item" key={sym} data-testid={`ol-pt-${sym}`}>
                      <img src={symbolSrc(sym)} alt={sym} />
                      <span>{mult}×</span>
                    </div>
                  ))}
                </div>
                <button className="ol-modal-close" onClick={() => setMenuView("root")} data-testid="ol-paytable-close">BACK</button>
              </div>
            )}
            {menuView === "rules" && (
              <div data-testid="ol-rules-modal">
                <h3>GAME RULES</h3>
                <p className="ol-modal-note">Paid play pays on the single center payline. BONUS symbols start Gold
                  Rush Free Plays, which pay on five paylines. The Gold Hand can nudge a reel into view. Jackpots:
                  MINI, MINOR, MAJOR and MEGA — MAJOR &amp; MEGA require a total wager of at least
                  {" "}{money(pools?.min_bet_by_tier?.MAJOR || 1)}. Game mode: {cfg.mode === "online" ? "online shared jackpots" : "standalone jackpots"}.</p>
                <button className="ol-modal-close" onClick={() => setMenuView("root")} data-testid="ol-rules-close">BACK</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
